import React from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useColorModeValue,
  Text,
  Avatar,
  Spacer,
  Image,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  useBreakpointValue,
  Badge,
  Divider,  // Add this import
} from '@chakra-ui/react';
import { ChevronDownIcon, HamburgerIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { navItemsFor } from '../../utils/access';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // "Démo check-in" (#59): admins only, and only where the backend has the feature (GET is not 404).
  // Production answers 404, so the entry stays hidden there without any front-end env flag.
  const demoQ = useQuery({
    queryKey: ['checkin-demo-available'],
    queryFn: () => apiService.getCheckinSimulation(),
    enabled: user?.role === 'admin',
    retry: false,
  });
  const showDemo = user?.role === 'admin' && demoQ.isSuccess;

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Detect if we're on mobile
  const isMobile = useBreakpointValue({ base: true, lg: false });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose(); // Close drawer on mobile after navigation
    }
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Navigation items configuration
  // Per role (#71): admin sees everything; event manager has no Contacts / CRM / palette / accounts;
  // front desk (viewer) only has its events.
  const navItems = navItemsFor(user?.role);

  // Mobile Drawer Menu
  const MobileMenu = () => (
    <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <HStack spacing={3}>
            <Image
              src="/logo_ffm.svg"
              alt="FFM"
              h="32px"
              w="auto"
              fallback={<Box w="32px" h="32px" bg="blue.500" borderRadius="md" />}
            />
            <Text fontSize="md" fontWeight="bold">Menu</Text>
          </HStack>
        </DrawerHeader>

        <DrawerBody px={0}>
          <VStack spacing={0} align="stretch">
            {/* User Info Card */}
            <Box p={4} bg="gray.50" borderBottomWidth="1px">
              <HStack spacing={3}>
                <Avatar
                  size="sm"
                  name={user?.full_name}
                  bg="blue.500"
                />
                <VStack align="start" spacing={0} flex="1">
                  <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                    {user?.full_name}
                  </Text>
                  <Text fontSize="xs" color="gray.600" noOfLines={1}>
                    {user?.email}
                  </Text>
                  <Badge 
                    colorScheme="blue" 
                    fontSize="xs" 
                    mt={1}
                    textTransform="capitalize"
                  >
                    {user?.role}
                  </Badge>
                </VStack>
              </HStack>
            </Box>

            {/* Navigation Items */}
            <VStack spacing={0} align="stretch" py={2}>
              {navItems.map((item) => {
                const active = isActive(item.path);

                return (
                  <Button
                    key={item.path}
                    justifyContent="flex-start"
                    variant="ghost"
                    colorScheme={active ? 'blue' : 'gray'}
                    bg={active ? 'blue.50' : 'transparent'}
                    fontWeight={active ? 'semibold' : 'normal'}
                    borderRadius="none"
                    py={6}
                    px={6}
                    onClick={() => handleNavigation(item.path)}
                    _hover={{
                      bg: active ? 'blue.100' : 'gray.100',
                    }}
                    _active={{
                      bg: active ? 'blue.100' : 'gray.200',
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </VStack>

            {/* Changed from MenuDivider to Divider */}
            <Divider />

            {/* Profile Link */}
            <Box p={4} borderBottomWidth="1px">
              {showDemo && (
                <Button
                  variant="ghost"
                  w="full"
                  justifyContent="flex-start"
                  onClick={() => handleNavigation('/demo/checkin')}
                  data-testid="menu-demo-checkin-mobile"
                >
                  Démo check-in
                </Button>
              )}
              <Button
                variant="ghost"
                w="full"
                justifyContent="flex-start"
                onClick={() => handleNavigation('/profile')}
              >
                Mon profil
              </Button>
            </Box>

            {/* Logout Button */}
            <Box p={4}>
              <Button
                colorScheme="red"
                variant="outline"
                w="full"
                onClick={() => {
                  handleLogout();
                  onClose();
                }}
              >
                Se déconnecter
              </Button>
            </Box>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );

  return (
    <Flex minH="100vh" direction="column">
      {/* Header */}
      <Box
        bg={bgColor}
        borderBottom="1px"
        borderColor={borderColor}
        px={{ base: 4, md: 6 }}
        py={3}
        shadow="sm"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Flex align="center">
          {/* Mobile: Hamburger Menu */}
          {isMobile && (
            <IconButton
              icon={<HamburgerIcon />}
              aria-label="Menu"
              variant="ghost"
              size="md"
              onClick={onOpen}
              mr={3}
            />
          )}

          {/* Logo */}
          <Box
            cursor="pointer"
            onClick={() => handleNavigation('/')}
            h={{ base: '36px', md: '44px' }}
            lineHeight={0}
            flexShrink={0}
          >
            <Image
              src="/logo_ffm.svg"
              alt="FFM"
              h="100%"
              w="auto"
              display="block"
            />
          </Box>

          <Spacer />

          {/* Desktop Navigation Menu */}
          {!isMobile && (
            <HStack spacing={4} mr={6}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  variant={isActive(item.path) ? 'solid' : 'ghost'}
                  colorScheme={isActive(item.path) ? 'blue' : 'gray'}
                  onClick={() => handleNavigation(item.path)}
                  size="sm"
                >
                  {item.label}
                </Button>
              ))}
            </HStack>
          )}

          {/* Desktop User Menu */}
          {!isMobile ? (
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                rightIcon={<ChevronDownIcon />}
                _hover={{ bg: 'gray.100' }}
                size="sm"
              >
                <HStack spacing={3}>
                  <Avatar size="sm" name={user?.full_name} />
                  <VStack 
                    align="start" 
                    spacing={0} 
                    display={{ base: 'none', xl: 'flex' }}
                  >
                    <Text fontSize="sm" fontWeight="medium">
                      {user?.first_name}
                    </Text>
                    <Text fontSize="xs" color="gray.500" textTransform="capitalize">
                      {user?.role}
                    </Text>
                  </VStack>
                </HStack>
              </MenuButton>
              <MenuList>
                <Box px={3} py={2}>
                  <Text fontWeight="bold" fontSize="sm">{user?.full_name}</Text>
                  <Text fontSize="sm" color="gray.600">{user?.email}</Text>
                  <Text fontSize="xs" color="blue.500" textTransform="capitalize">
                    {user?.role}
                  </Text>
                </Box>
                <MenuDivider />
                <MenuItem onClick={() => handleNavigation('/profile')}>
                  Mon profil
                </MenuItem>
                {showDemo && (
                  <MenuItem onClick={() => handleNavigation('/demo/checkin')} data-testid="menu-demo-checkin">
                    Démo check-in
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogout}>
                  Se déconnecter
                </MenuItem>
              </MenuList>
            </Menu>
          ) : (
            /* Mobile: Just show avatar */
            <Avatar 
              size="sm" 
              name={user?.full_name}
              bg="blue.500"
            />
          )}
        </Flex>
      </Box>

      {/* Mobile Drawer */}
      {isMobile && <MobileMenu />}

      {/* Main Content */}
      <Box flex="1" p={{ base: 4, md: 6 }} bg="gray.50">
        {children}
      </Box>
    </Flex>
  );
};